// react
import { ChangeEvent, MouseEvent, useEffect, useMemo, useState } from 'react';

// mui
import {
	Box,
	Grid,
	InputAdornment,
	InputLabel,
	SelectChangeEvent,
	Typography,
} from '@mui/material';
import { SxProps } from '@mui/system';

// ui
import { IconCircleLoan } from '../../ui/IconCircleLoan/IconCircleLoan';

// i18n
import { useTranslation } from 'react-i18next';

// fields
import { Input, InputProps } from '../Input/Input';

// theme
import { theme } from '../../../theme';

// utils
import { constructAmountInputs } from './Amounts.utils';
import { formatCurrency } from '../../../utils/currency';
import { SelectCurrency } from '../SelectCurrency/SelectCurrency';

// types
import { AccountBalance, ExchangeRateByDate, Loan } from '../../../@types';

// styling
const sxStyles = {
	boxContainer: { border: `1px ${theme.palette.divider} solid`, borderRadius: '5px' },
	boxRow: {
		padding: theme.spacing(2),
		borderBottom: `1px ${theme.palette.divider} solid`,
		minWidth: '100%',
	},
};

// props
export interface AmountsSummary {
	operator?: '+' | '-' | '*';
	title: string;
	value: number;
}

export interface AmountsValue {
	amount: number;
	loan: Loan;
	tax: number;
}

export interface AmountsProps {
	balances?: AccountBalance[];
	currency?: string;
	exchangeRates?: ExchangeRateByDate[];
	formId: string;
	hasCurrency?: boolean;
	hasTax?: boolean;
	inputs?: AmountsInputProps[];
	isReadOnly?: boolean;
	isRequired?: boolean;
	loans?: Loan[];
	onChange?: (
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		total: number,
		loan: Loan
	) => void;
	onChangeCurrency?: (e: SelectChangeEvent<unknown>) => void;
	summaries?: AmountsSummary[];
	title: string;
	titleTotal?: string;
	values?: AmountsValue[];
}
export type AmountsInputProps = {
	balance?: number;
	loan: Loan;
} & InputProps;

export interface SourceTotal {
	amount: number;
	tax: number;
}

export interface AmountsTotals {
	sources: Record<number, SourceTotal>;
	total: number;
}

export const Amounts = ({
	balances = [],
	currency = 'USD',
	exchangeRates = [],
	formId,
	hasCurrency = true,
	hasTax = false,
	inputs,
	isReadOnly = false,
	isRequired = true,
	loans = [],
	onChange,
	onChangeCurrency,
	summaries = [],
	title,
	titleTotal,
	values,
}: AmountsProps) => {
	// hooks
	const { t } = useTranslation();

	// defaults
	const defaultTotals = useMemo(() => {
		const sources: Record<number, SourceTotal> = {};
		let total = 0;

		if (values && Array.isArray(values)) {
			values.forEach((valueObj, index) => {
				sources[index] = {
					amount: valueObj.amount || 0,
					tax: valueObj.tax || 0,
				};
				total += sources[index].amount + sources[index].tax;
			});
		}
		return {
			sources,
			total,
		};
	}, [values]);

	// state
	const [totals, setTotals] = useState<AmountsTotals>(defaultTotals);

	// vars
	let amountsInputs: AmountsInputProps[] = [];

	if (inputs) {
		amountsInputs = inputs;
	} else if (balances.length) {
		amountsInputs = constructAmountInputs({ balances, values });
	} else if (loans.length) {
		amountsInputs = constructAmountInputs({ loans, values });
	}

	const summaryLineItems: AmountsSummary[] = [
		{
			title: titleTotal || t('totalAmount'),
			value: totals.total,
		},
	].concat(summaries);

	const calcTotals = () => {
		const form = document.querySelector(`#${formId}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());
		const totalsCalculated: AmountsTotals = defaultTotals;

		for (let i = 0; i < amountsInputs.length; i++) {
			const amount = Number(entries[`source-${i}-amount`] || 0);
			const tax = Number(entries[`source-${i}-tax`] || 0);

			totalsCalculated.sources[i] = {
				amount,
				tax,
			};
		}

		totalsCalculated.total = Object.values(totalsCalculated.sources).reduce((acc, source) => {
			return acc + source.amount + source.tax;
		}, 0);

		return totalsCalculated;
	};

	const amountsNumOfCols = 12 / (exchangeRates.length + 1);

	// handlers
	const handleChangeSource = (
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		loan: Loan
	) => {
		const totalsNew = calcTotals();

		if (onChange) {
			onChange(e, totals.total, loan);
		}

		setTotals(totalsNew);
	};

	// clear inputs when loans change
	useEffect(() => {
		if (loans && !values) {
			for (let i = 0; i < loans.length; i++) {
				const inputAmount = document.getElementsByName(`source-${i}-amount`)[0] as HTMLInputElement;
				const inputTax = document.getElementsByName(`source-${i}-tax`)[0] as HTMLInputElement;

				if (inputAmount) {
					inputAmount.value = '';
				}

				if (inputTax) {
					inputTax.value = '';
				}
			}
			setTotals(defaultTotals);
		}
	}, [defaultTotals, loans, values]);

	return (
		<>
			<InputLabel required={isRequired}>{title}</InputLabel>
			<Box sx={sxStyles.boxContainer}>
				{hasCurrency && (
					<Box
						sx={{
							...sxStyles.boxRow,
							borderTopLeftRadius: 8,
							borderTopRightRadius: 8,
							backgroundColor: theme.palette.brand.grey[100],
						}}>
						<Grid container alignItems={'center'}>
							<Grid item xs={8}>
								<Typography fontWeight={600}>{t('currency')}</Typography>
							</Grid>

							<Grid item xs={4}>
								<SelectCurrency
									hasLabel={false}
									id={`${formId}-currency`}
									name="currency"
									onChange={(e: SelectChangeEvent<unknown>) => {
										if (onChangeCurrency) {
											onChangeCurrency(e);
										}
									}}
									required={true}
									value={currency}
								/>
							</Grid>
						</Grid>
					</Box>
				)}
				<Grid container>
					{amountsInputs.map((input: AmountsInputProps, indexInput: number) => {
						const { balance, disabled, loan } = input;
						const hasConversions = exchangeRates.length > 0;

						let helperText: InputProps['helperText'];

						if (typeof balance === 'number') {
							helperText = t('amountAvailable', {
								amount: formatCurrency({
									currency,
									value: balance,
								}),
							});
						}

						return (
							<Box key={`source=${indexInput}`} sx={sxStyles.boxRow}>
								<Grid container justifyContent="space-between" spacing={2}>
									<Grid item xs={12} lg={hasConversions ? 12 : 6}>
										<Grid alignItems="center" container spacing={1}>
											<Grid item xs="auto">
												<IconCircleLoan loan={loan} />
											</Grid>
											<Grid item xs="auto">
												<InputLabel
													disabled={disabled}
													htmlFor={`${formId}-source-${indexInput}-amount`}
													sx={{ margin: 0 }}>
													{loan.code}
												</InputLabel>
											</Grid>
										</Grid>
									</Grid>

									<Grid item xs={12} lg={hasConversions ? 12 : 6}>
										<Grid container spacing={2}>
											<Grid item xs={12}>
												<Grid container>
													<Grid item xs={12}>
														<InputLabel htmlFor={`${formId}-source-${indexInput}-amount`}>
															{t('amount')}
														</InputLabel>
													</Grid>
													<Grid item xs={12}>
														<Grid container spacing={2}>
															<Grid item xs={12} md={hasConversions ? amountsNumOfCols : 12}>
																<Input
																	currency={currency}
																	disabled={disabled}
																	helperText={helperText}
																	id={`${formId}-source-${indexInput}-amount`}
																	inputProps={{
																		'data-index': indexInput,
																	}}
																	InputProps={{
																		readOnly: isReadOnly,
																	}}
																	name={`source-${indexInput}-amount`}
																	onWheel={(e: MouseEvent) => {
																		const target = e.target as HTMLElement;
																		return target.blur();
																	}}
																	onChange={(e) => handleChangeSource(e, loan)}
																	type="number"
																	value={totals.sources[indexInput]?.amount || ''}
																/>
															</Grid>
															{hasConversions && (
																<>
																	{exchangeRates.map((exchangeRate, indexRate) => (
																		<Grid
																			item
																			xs={12}
																			md={amountsNumOfCols}
																			key={`conversion-amount-${indexInput}-${indexRate}`}>
																			<Input
																				currency={exchangeRate.currency}
																				id={`${formId}-source-${indexInput}-amount-conversion-${indexRate}`}
																				inputProps={{
																					'data-index': indexInput,
																					'data-index-conversion': indexRate,
																				}}
																				InputProps={{
																					readOnly: true,
																					startAdornment: (
																						<InputAdornment position="start">=</InputAdornment>
																					),
																				}}
																				name={`source-${indexInput}-amount-conversion-${indexRate}`}
																				type="number"
																				value={(() => {
																					// Get the current amount from totals
																					const sourceAmount =
																						totals.sources[indexInput]?.amount || 0;
																					return exchangeRate.exchangeRate
																						? (sourceAmount / exchangeRate.exchangeRate).toFixed(2)
																						: 0;
																				})()}
																			/>
																		</Grid>
																	))}
																</>
															)}
														</Grid>
													</Grid>
												</Grid>
											</Grid>

											{hasTax && (
												<Grid item xs={12}>
													<Grid container>
														<Grid item xs={12}>
															<InputLabel htmlFor={`${formId}-source-${indexInput}-tax`}>
																{t('tax')}
															</InputLabel>
														</Grid>
														<Grid item xs={12}>
															<Grid container spacing={2}>
																<Grid item xs={12} md={hasConversions ? amountsNumOfCols : 12}>
																	<Input
																		currency={currency}
																		id={`${formId}-source-${indexInput}-tax`}
																		inputProps={{
																			'data-index': indexInput,
																		}}
																		InputProps={{
																			readOnly: isReadOnly,
																		}}
																		name={`source-${indexInput}-tax`}
																		onWheel={(e: MouseEvent) => {
																			const target = e.target as HTMLElement;
																			return target.blur();
																		}}
																		onChange={(e) => handleChangeSource(e, loan)}
																		type="number"
																		value={totals.sources[indexInput]?.tax || ''}
																	/>
																</Grid>
																{hasConversions && (
																	<>
																		{exchangeRates.map((exchangeRate, indexRate) => (
																			<Grid
																				item
																				xs={12}
																				md={amountsNumOfCols}
																				key={`conversion-tax-${indexInput}-${indexRate}`}>
																				<Input
																					currency={exchangeRate.currency}
																					id={`${formId}-source-${indexInput}-tax-conversion-${indexRate}`}
																					inputProps={{
																						'data-index': indexInput,
																						'data-index-conversion': indexRate,
																					}}
																					InputProps={{
																						readOnly: true,
																						startAdornment: (
																							<InputAdornment position="start">=</InputAdornment>
																						),
																					}}
																					name={`source-${indexInput}-tax-conversion-${indexRate}`}
																					type="number"
																					value={(() => {
																						// Get the current tax from totals
																						const sourceTax = totals.sources[indexInput]?.tax || 0;
																						return exchangeRate.exchangeRate
																							? (sourceTax / exchangeRate.exchangeRate).toFixed(2)
																							: 0;
																					})()}
																				/>
																			</Grid>
																		))}
																	</>
																)}
															</Grid>
														</Grid>
													</Grid>
												</Grid>
											)}
											<Grid item xs={12} lg={12}>
												<Typography color="brand.grey.500">
													{formatCurrency({
														currency,
														value:
															totals.sources[indexInput]?.amount +
																totals.sources[indexInput]?.tax || 0,
													})}
												</Typography>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Box>
						);
					})}
					{summaryLineItems.map((summary, indexSummary) => {
						const sxBox: SxProps = {
							...sxStyles.boxRow,
							backgroundColor: theme.palette.brand.grey[100],
						};
						const isLast = indexSummary === summaryLineItems.length - 1;

						let value = summary.value;
						let color = `brand.grey.500`;

						if (summary.operator) {
							if (summary.operator === '+') {
								value = value + totals.total;
							} else if (summary.operator === '-') {
								value = value - totals.total;
							} else if (summary.operator === '*') {
								value = value * totals.total;
							}
						}

						if (isLast) {
							sxBox.border = 'none';
							sxBox.borderBottomLeftRadius = 8;
							sxBox.borderBottomRightRadius = 8;
							color = `text.primary`;
						}

						return (
							<Box key={`summary-${indexSummary}-${summary.value}`} sx={sxBox}>
								<Grid alignItems="center" container justifyContent="space-between" spacing={2}>
									<Grid item xs={6}>
										<Typography color={color} fontWeight={600}>
											{summary.title}
										</Typography>
									</Grid>
									<Grid item xs="auto">
										<Typography color={color} fontWeight={600}>
											{formatCurrency({
												currency,
												value,
											})}
										</Typography>
									</Grid>
								</Grid>
							</Box>
						);
					})}
				</Grid>
			</Box>
		</>
	);
};
